import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from 'react'
import {
  type IssueSelection,
  NO_ISSUE_SELECTION,
  hasIssueSelection,
} from './issues/issueCounts'
import {
  type MetricFilters,
  NO_METRIC_FILTERS,
  hasMetricFilters,
} from './metricFilters'

/**
 * Everything the orders dashboard is currently narrowed by: the partner and
 * event picked in the metric grids, and the severity and issue type picked in
 * the count cards.
 *
 * Held above the dashboard rather than inside it because the page header --
 * which owns the range picker, and the button that clears all of this -- is
 * the dashboard's parent. The window itself is not a filter: there is always
 * one, and clearing it would leave nothing to report on.
 */
export interface OrderDashboardFilters {
  metrics: MetricFilters
  setMetrics: Dispatch<SetStateAction<MetricFilters>>
  issues: IssueSelection
  setIssues: (selection: IssueSelection) => void
  /** Whether anything is narrowed, i.e. whether clearing would do something. */
  active: boolean
  clear: () => void
}

export function useOrderDashboardFilters(): OrderDashboardFilters {
  const [metrics, setMetrics] = useState<MetricFilters>(NO_METRIC_FILTERS)
  const [issues, setIssues] = useState<IssueSelection>(NO_ISSUE_SELECTION)

  const clear = useCallback(() => {
    setMetrics(NO_METRIC_FILTERS)
    setIssues(NO_ISSUE_SELECTION)
  }, [])

  return {
    metrics,
    setMetrics,
    issues,
    setIssues,
    active: hasMetricFilters(metrics) || hasIssueSelection(issues),
    clear,
  }
}
